import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { History, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CharacterSessionHistory — Saga20-style feed of sessions where a character appears.
 * Uses simple substring match on the character's name in session entries/recaps.
 *
 * Props:
 *   characterId — id of the character or NPC
 *   onJump — optional callback when a session is clicked. If absent, navigates.
 */
export const CharacterSessionHistory = ({ characterId, onJump }) => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!characterId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/characters/${characterId}/sessions`, { withCredentials: true })
      .then(r => { if (!cancelled) setSessions(r.data || []); })
      .catch(() => { if (!cancelled) setSessions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [characterId]);

  if (loading) return null;

  return (
    <div className="mt-5" data-testid="character-session-history">
      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5" /> Session Appearances
        <span className="text-[10px] text-muted-foreground/60 normal-case">({sessions.length})</span>
      </h4>
      {sessions.length === 0 ? (
        <p className="text-xs text-muted-foreground/60 italic">No session mentions yet. Add notes referencing this character to populate this list.</p>
      ) : (
        <div className="space-y-1.5">
          {sessions.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                if (onJump) onJump(s);
                else navigate(`/campaign/${campaignId}/sessions/${s.id}`);
              }}
              className="w-full text-left p-2 rounded-sm border border-border bg-card hover:border-primary/40 hover:bg-muted/10 transition-colors group flex items-start gap-2"
              data-testid={`character-session-${s.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">{s.title}</p>
                <p className="text-[10px] text-muted-foreground tabular-nums">
                  {s.started_at ? new Date(s.started_at).toLocaleDateString() : ""}
                  {s.entry_count > 0 ? ` · ${s.entry_count} entr${s.entry_count === 1 ? "y" : "ies"}` : ""}
                </p>
                {s.recap && <p className="text-xs text-muted-foreground/80 line-clamp-2 mt-1">{s.recap}…</p>}
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-primary flex-shrink-0 mt-0.5" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CharacterSessionHistory;
